import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { budgetsApi, forecastsApi } from '../../services/api';
import { generateStandardName, getBuCode, sortFiscalYearsNewestFirst } from '../../lib/namingUtils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Building2, 
  Calendar as CalendarIcon, 
  FileText, 
  TrendingUp,
  RefreshCw 
} from 'lucide-react';

/**
 * Create Budget Dialog Component
 * 
 * Props:
 * - open: Boolean to control dialog visibility
 * - onOpenChange: Function to handle dialog open/close
 * - businessUnits: Array of business units
 * - fiscalYearOptions: Array of fiscal year strings
 * - onSuccess: Callback when budget is created successfully (receives budget data)
 */
const CreateBudgetDialog = ({ 
  open, 
  onOpenChange, 
  businessUnits = [],
  fiscalYearOptions = [],
  onSuccess 
}) => {
  const { isDark } = useTheme();
  
  const [name, setName] = useState('');
  const [buId, setBuId] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [source, setSource] = useState('scratch'); // 'scratch' or 'forecast'
  const [selectedForecastId, setSelectedForecastId] = useState('');
  const [availableForecasts, setAvailableForecasts] = useState([]);
  const [creating, setCreating] = useState(false);

  // Auto-generate budget name when BU or FY changes
  useEffect(() => {
    if (buId && fiscalYear) {
      const selectedBu = businessUnits.find(bu => bu.id === buId);
      const buCode = getBuCode(selectedBu);
      const generatedName = generateStandardName(buCode, fiscalYear, 'Budget');
      setName(generatedName);
    }
  }, [buId, fiscalYear, businessUnits]);

  // Load available forecasts when BU changes
  useEffect(() => {
    const loadForecasts = async () => {
      if (buId) {
        try {
          const res = await forecastsApi.getAll({ business_unit_id: buId });
          const activeForecasts = res.data.filter(f => f.status === 'active' || f.status === 'archived');
          setAvailableForecasts(activeForecasts);
        } catch (error) {
          console.error('Failed to load forecasts:', error);
          setAvailableForecasts([]);
        }
      } else {
        setAvailableForecasts([]);
      }
    };
    loadForecasts();
  }, [buId]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a budget name');
      return;
    }
    if (!buId) {
      toast.error('Please select a Business Unit');
      return;
    }
    if (!fiscalYear) {
      toast.error('Please select a Fiscal Year');
      return;
    }
    if (source === 'forecast' && !selectedForecastId) {
      toast.error('Please select a forecast to copy from');
      return;
    }

    setCreating(true);
    try {
      const bu = businessUnits.find(b => b.id === buId);
      const response = await budgetsApi.importFromFile({
        name,
        business_unit_id: buId,
        business_unit_name: bu?.name || '',
        fiscal_year: fiscalYear,
        source_forecast_id: source === 'forecast' ? selectedForecastId : null
      });
      
      const sourceMsg = source === 'forecast' ? ' from forecast' : '';
      toast.success(`Budget "${name}" created${sourceMsg}!`);
      
      // Reset form
      resetForm();
      onOpenChange(false);
      
      // Call success callback
      if (onSuccess && response.data?.id) {
        onSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create budget');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setName('');
    setBuId('');
    setFiscalYear('');
    setSource('scratch');
    setSelectedForecastId('');
    setAvailableForecasts([]);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`sm:max-w-lg rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-600" />
            Create New Budget
          </DialogTitle>
          <DialogDescription>
            Create a new budget from scratch or from an existing forecast.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Business Unit */}
          <div className="space-y-2">
            <Label>Business Unit *</Label>
            <Select value={buId} onValueChange={setBuId} disabled={creating}>
              <SelectTrigger className="rounded-sm" data-testid="create-budget-bu">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Select Business Unit" />
              </SelectTrigger>
              <SelectContent>
                {businessUnits.map(bu => (
                  <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fiscal Year */}
          <div className="space-y-2">
            <Label>Fiscal Year *</Label>
            <Select value={fiscalYear} onValueChange={setFiscalYear} disabled={creating}>
              <SelectTrigger className="rounded-sm">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Select Fiscal Year" />
              </SelectTrigger>
              <SelectContent>
                {sortFiscalYearsNewestFirst(fiscalYearOptions).map(fy => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Budget Name */}
          <div className="space-y-2">
            <Label>Budget Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Budget name (auto-generated)"
              className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              disabled={creating}
            />
          </div>

          {/* Source Selection */}
          <div className="space-y-3">
            <Label>Create From</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setSource('scratch');
                  setSelectedForecastId('');
                }}
                disabled={creating}
                className={`p-3 rounded-sm border-2 text-left transition-all ${
                  source === 'scratch'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FileText className={`w-5 h-5 mb-1 ${source === 'scratch' ? 'text-emerald-600' : 'text-slate-400'}`} />
                <p className={`text-sm font-medium ${source === 'scratch' ? 'text-emerald-700 dark:text-emerald-400' : ''}`}>
                  Start from Scratch
                </p>
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Empty template
                </p>
              </button>
              
              <button
                type="button"
                onClick={() => setSource('forecast')}
                disabled={creating || availableForecasts.length === 0}
                className={`p-3 rounded-sm border-2 text-left transition-all ${
                  source === 'forecast'
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                    : availableForecasts.length === 0
                      ? 'opacity-50 cursor-not-allowed border-slate-200 dark:border-zinc-700'
                      : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <TrendingUp className={`w-5 h-5 mb-1 ${source === 'forecast' ? 'text-violet-600' : 'text-slate-400'}`} />
                <p className={`text-sm font-medium ${source === 'forecast' ? 'text-violet-700 dark:text-violet-400' : ''}`}>
                  From Forecast
                </p>
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                  {availableForecasts.length > 0 ? `${availableForecasts.length} available` : 'No forecasts'}
                </p>
              </button>
            </div>
            
            {/* Forecast Selection */}
            {source === 'forecast' && availableForecasts.length > 0 && (
              <div className="space-y-2 mt-2">
                <Label>Select Forecast *</Label>
                <Select value={selectedForecastId} onValueChange={setSelectedForecastId} disabled={creating}>
                  <SelectTrigger className="rounded-sm">
                    <TrendingUp className="w-4 h-4 mr-2 text-violet-500" />
                    <SelectValue placeholder="Select a forecast" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableForecasts.map(forecast => (
                      <SelectItem key={forecast.id} value={forecast.id}>
                        <div className="flex items-center gap-2">
                          <span>{forecast.name}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            forecast.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {forecast.status}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Info Note */}
          <div className={`p-3 rounded-sm text-sm ${isDark ? 'bg-emerald-900/20 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}>
            <strong>Note:</strong> {source === 'forecast' 
              ? 'Budget will be created from the selected forecast with all line items copied.'
              : 'Budget will be created as Draft using the Chart of Accounts template.'
            } You can edit figures and submit for approval when ready.
          </div>
        </div>
        
        <DialogFooter className="gap-2 mt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            className="rounded-sm"
            disabled={creating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={creating}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Budget
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBudgetDialog;
